import { useMemo } from "react"
import { clamp } from "lodash"
import { DEFAULT_VENUE_FILTER } from "constant"
import { useVenue } from "providers/venue"
import { useIndoorMap } from "providers/venue/modules/indoormap/hooks/useIndoorMap"
import { getSuitablyValueBetweenBearings } from "components/IndoorMap"
import { useGeoLocation } from "providers/geolocation"
import { IBaseFlyToOptions } from "hooks/map"

export type UseGetMapViewFromElectronConfig = (
  viewingVenueId?: string,
  ordinal?: number
) => IBaseFlyToOptions

export const useGetMapViewFromElectronConfig: UseGetMapViewFromElectronConfig =
  (viewingVenueId, ordinal) => {
    const { dataLoaded } = useVenue()
    const { indoorRef, mapLoaded } = useIndoorMap()
    const { viewPoint, locationVenue } = useGeoLocation()

    const mapReady = indoorRef?.current && mapLoaded && dataLoaded

    const electronMapView = useMemo(() => {
      const isSameVenue = viewingVenueId === locationVenue?.id
      if (!mapReady || !viewPoint || !isSameVenue) return null

      const indoorMap = indoorRef.current
      const currentBearing = indoorMap.getBearing()
      const targetBearing = indoorRef.current.defaultBearing
      const bearing = getSuitablyValueBetweenBearings(
        targetBearing,
        currentBearing
      )
      const pitch = indoorRef.current.defaultPitch
      const locationExtent = indoorMap.getFeatureExtent(viewPoint)
      // Check if location's venue has geometry before retrieving feature extent to prevent function errors
      const canCalculateFeatureExtent =
        locationVenue?.id && locationVenue?.id !== DEFAULT_VENUE_FILTER
      const venueExtent = canCalculateFeatureExtent
        ? indoorMap.getFeatureExtent(locationVenue)
        : null
      const center = indoorMap.getExtentCenter(locationExtent)
      const defaultZoom =
        indoorRef.current.defaultZoom ?? indoorMap.getExtentZoom(venueExtent)
      const zoom = clamp(defaultZoom, 19.5, 21)

      return {
        center,
        zoom,
        bearing,
        pitch,
      }
    }, [indoorRef, viewPoint, locationVenue, viewingVenueId, mapReady])

    return electronMapView
  }
